import React, { useState } from 'react';
import { Animated, StyleSheet, View } from 'react-native';

import { IS_WEB } from 'utils/relicVault/webStyle';
import { useBreathing, useShimmerSweep } from './useRelicVaultAnimations';

/**
 * The featured-art treatment from the purchase modal (.modal-icon) and the
 * relic detail popup (.detail-art-wrap):
 *   - ::before  a white diagonal highlight that sweeps across once (1.2s)
 *   - ::after   an inset glow that breathes in the rarity color (1.8s loop)
 *
 * Render as the last child of a clipped (overflow: hidden) container.
 * `replayKey` restarts the sweep, e.g. when a different relic opens.
 */
const ShimmerHalo = ({ glow, radius = 0, haloSize = 24, haloSpread = 4, replayKey, delay }) => {
  const [box, setBox] = useState({ w: 0, h: 0 });
  const sweep = useShimmerSweep(replayKey, delay);
  const halo = useBreathing(1800);

  const sweepOpacity = sweep.interpolate({
    inputRange: [0, 0.1, 0.55, 1],
    outputRange: [0, 1, 1, 0],
  });
  // CSS moves a 250%-sized gradient from -120% to 160%: the highlight band
  // travels from beyond the top-left corner to beyond the bottom-right.
  const travelX = sweep.interpolate({
    inputRange: [0, 1],
    outputRange: [-box.w, box.w],
  });
  const travelY = sweep.interpolate({
    inputRange: [0, 1],
    outputRange: [-box.h, box.h],
  });

  return (
    <View
      pointerEvents="none"
      style={[StyleSheet.absoluteFill, { borderRadius: radius, overflow: 'hidden' }]}
      onLayout={e => {
        const { width, height } = e.nativeEvent.layout;
        if (width !== box.w || height !== box.h) setBox({ w: width, h: height });
      }}>
      {box.w > 0 && (
        <Animated.View
          style={[
            StyleSheet.absoluteFill,
            {
              opacity: sweepOpacity,
              transform: [{ translateX: travelX }, { translateY: travelY }],
            },
            IS_WEB
              ? {
                  backgroundImage:
                    'linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.55) 50%, transparent 65%)',
                }
              : styles.sweepNative,
          ]}
        />
      )}
      <Animated.View
        style={[
          StyleSheet.absoluteFill,
          { borderRadius: radius, opacity: halo },
          IS_WEB
            ? { boxShadow: `inset 0 0 ${haloSize}px ${haloSpread}px ${glow}` }
            : { borderWidth: 2, borderColor: glow },
        ]}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  // Native has no CSS gradient: a soft translucent band approximates the highlight.
  sweepNative: {
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
});

export default ShimmerHalo;
